import { GridApi, RowNode } from 'ag-grid-community';
import { Dispatch } from 'react';
import { applyTransaction, collapseAboveNode, expandAboveNode } from '../../helper/helperFunctions';
import { deleteRowsFromGrid, determineRowsToDelete, getPredecessorColumnUsingGridRef, getSelectedNodes, getUniqueParentSetOfAllRows, handleEmptyGrid } from '../../helper/callbackHelper';
import { parentRowsAferRollup } from '../../helper/helpersSubModule';
import { context, trace } from '@opentelemetry/api';
import { logSpan } from '../../../../helpers/loggingHelper';

interface PrepareRowDeletionParams {
    gridRef: any;
    insertRow: () => void;
    setSelectedCellHelper: (rowIndex: number, colKey: string) => void;
    dispatch: Dispatch<any>;
    setSuccessorNodesForRowUndo: () => void,
    predecessorColumn: Object,
    cellToDelete?:{rowIndex:number}
}

interface UndoData {
    rowsToDelete: any[];
    selectedRowIndexes: number[];
    successorNodesForRowUndoForRowDelete: any[];
    NodeToCollapseNode: RowNode | null;
    extraRowsForPredecessor: any[];
    undoRowsForPredecessor: any[];
    parentRowsAffectedByRollup: any[];
    addedRowsAfterDeletion: any[];
}

interface PrepareRowDeletionResponse {
    undoData: UndoData | null;
    error?: string;
}

const getIndexesFromRowIds = (rowIds: string[], gridRef: any): RowNode[] => {
    return rowIds
        .map(id => gridRef.current.api.getRowNode(id))
        .filter((row): row is RowNode => !!row)
        .sort((a, b) => a.sourceRowIndex - b.sourceRowIndex);
};
export const prepareRowDeletion = async (
    gridRef: any,
    insertRow: () => void,
    setSelectedCellHelper: (rowIndex: number, colKey: string) => void,
    dispatch: Dispatch<any>,
    setSuccessorNodesForRowUndo: () => void,
    predecessorColumn: Object,
    rowsIdsToDelete?:string[]
): Promise<PrepareRowDeletionResponse> => {
    const activeSpan = trace.getSpan(context.active());
    let functionFlow = ["prepareRowDeletion started"];
    try {
        const currentSheetRows = gridRef.current.api.getModel().rowsToDisplay as RowNode[];
        const cell =  gridRef.current.api.getFocusedCell();
        if (!cell && !rowsIdsToDelete) {
            functionFlow.push("No focused cell and no row IDs provided, throwing error");
            throw new Error('No cell is currently focused.');
        }
        let nodeStateMap = new Map<string, any>();
        let selectedNodes = getSelectedNodes(gridRef,currentSheetRows);
        if(rowsIdsToDelete && rowsIdsToDelete.length > 0) {
            selectedNodes = getIndexesFromRowIds(rowsIdsToDelete,gridRef)
            functionFlow.push("Selected nodes derived from rowsIdsToDelete");
        }
        else if (selectedNodes.length === 0) {            
            if (cell != null) {
                selectedNodes = [currentSheetRows[cell.rowIndex]];
                functionFlow.push("No selected nodes found; defaulting to focused cell row");
            }
        }
        let updatedRowIndex = selectedNodes[0].rowIndex;
        let NodeToCollapse: number | null = null;

        // if (currentSheetRows[selectedNodes[0].rowIndex]?.allChildrenCount  && currentSheetRows[selectedNodes[0].rowIndex]?.expanded == false  ) {
        //     updatedRowIndex += currentSheetRows[selectedNodes[0].rowIndex].allChildrenCount;
        //     gridRef.current.api.setFocusedCell(updatedRowIndex, cell?.column?.getId() ?? 'ag-Grid-AutoColumn');
        //     functionFlow.push("Adjusted focused cell for collapsed group");
        // }

        if (
            currentSheetRows[selectedNodes[0].rowIndex - 1]?.expanded === false &&
            currentSheetRows[selectedNodes[0].rowIndex - 1]?.allChildrenCount !== 0
        ) {
            NodeToCollapse = selectedNodes[0].rowIndex - 1;
           // nodeStateMap = expandAboveNode(currentSheetRows[NodeToCollapse], gridRef);
           functionFlow.push("Node marked for collapse above current selection");
        }

        return new Promise((resolve) => {
            setTimeout(() => {
                try {
                    const predecessorCol = predecessorColumn || getPredecessorColumnUsingGridRef(gridRef);
                    functionFlow.push("Predecessor column determined");
                    const {
                        rowsToDelete,
                        selectedRowIndexes,
                        undoRowsForPredecessor,
                        extraRowsForPredecessor,
                        successorNodesForRowUndoForRowDelete,
                    } = determineRowsToDelete(selectedNodes, gridRef, setSuccessorNodesForRowUndo, predecessorCol);
                    functionFlow.push("Rows to delete determined");
                    if (rowsToDelete.length === 0) {
                        resolve({ undoData: null, error: 'No rows to delete' });
                        functionFlow.push("No rows to delete, resolving with error");
                        activeSpan?.setAttribute("prepareRowDeletion-functionFlow", functionFlow);
                        return;
                    }

                    const uniqueParentsOfAllRows = getUniqueParentSetOfAllRows(rowsToDelete, gridRef);
                    const deletedRows = deleteRowsFromGrid(gridRef, rowsToDelete);
                    functionFlow.push("Rows deleted from grid");

                    const {
                        parentRowsAffectedByRollup,
                        additionalParentAndExtraRowsForUndo,
                        mergedRowsAfterRollupAndPredecessorChange,
                    } = parentRowsAferRollup([], gridRef, predecessorColumn, uniqueParentsOfAllRows);
                    functionFlow.push("Post-deletion rollup processing done");

                    if (extraRowsForPredecessor?.length) {
                        applyTransaction(gridRef, extraRowsForPredecessor, extraRowsForPredecessor[0].rowIndex);
                    }

                    let addedRowsAfterDeletion: any[] = [];  
                    if(gridRef) handleEmptyGrid(gridRef, insertRow, setSelectedCellHelper, null, dispatch, activeSpan, (addedRows) => {
                        addedRowsAfterDeletion = addedRows;
                    });
                    functionFlow.push("Empty grid handling complete");
                    const undoData: UndoData = {
                        rowsToDelete,
                        selectedRowIndexes,
                        successorNodesForRowUndoForRowDelete,
                        NodeToCollapseNode: NodeToCollapse !== null ? currentSheetRows[NodeToCollapse] : null,
                        extraRowsForPredecessor,
                        undoRowsForPredecessor,
                        parentRowsAffectedByRollup,
                        addedRowsAfterDeletion,
                    };

                    activeSpan?.setAttribute("prepareRowDeletion-functionFlow", functionFlow);
                    setTimeout(() => {
                        gridRef.current.api.setFocusedCell(updatedRowIndex, cell?.column?.getId() ?? 'ag-Grid-AutoColumn');
                    }, 0);
                    resolve({ undoData });

                    // if (nodeStateMap.size > 0 && NodeToCollapse !== null) {
                    //     collapseAboveNode(currentSheetRows[NodeToCollapse], gridRef, nodeStateMap);
                    // }
                } catch (innerError) {
                    console.error('Error inside row deletion logic:', innerError);
                    functionFlow.push("Error inside row deletion logic");
                    activeSpan?.setAttribute("prepareRowDeletion-functionFlow", functionFlow);
                    resolve({ undoData: null, error: innerError.message });
                }
            }, 10);
        });
    } catch (error) {
        console.error('Error in prepareRowDeletion:', error);
        functionFlow.push("prepareRowDeletion error");
        logSpan("prepareRowDeletion-error", activeSpan?.spanContext(), "ERROR", error);
        activeSpan?.setAttribute("prepareRowDeletion-functionFlow", functionFlow);
        return { undoData: null, error: (error as Error).message };
    }
};
